import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Card, CardContent, Avatar, Divider, CircularProgress
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import api, { playerApi } from '../../services/api';
import type { Player, BatsmenInning, BowlerInning } from '../../models';

const PlayerView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<Partial<Player>>({});
  const [batInnings, setBatInnings] = useState<BatsmenInning[]>([]);
  const [bowlInnings, setBowlInnings] = useState<BowlerInning[]>([]);
  const [loadingBat, setLoadingBat] = useState(false);
  const [loadingBowl, setLoadingBowl] = useState(false);

  useEffect(() => {
    const fetchPlayer = async () => {
      setLoading(true);
      try {
        const res = await playerApi.getById(Number(id));
        setPlayer(res.data);
        setFormValues(res.data);
      } catch (e) {
        setPlayer(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPlayer();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchBatBowl = async () => {
      setLoadingBat(true);
      setLoadingBowl(true);
      try {
        const batRes = await api.get(`/cricket/players/${id}/last_batting_innings/`, { params: { limit: 10 } });
        setBatInnings(batRes.data || []);
      } catch {
        setBatInnings([]);
      } finally {
        setLoadingBat(false);
      }
      try {
        const bowlRes = await api.get(`/cricket/players/${id}/last_bowling_innings/`, { params: { limit: 10 } });
        setBowlInnings(bowlRes.data || []);
      } catch {
        setBowlInnings([]);
      } finally {
        setLoadingBowl(false);
      }
    };
    fetchBatBowl();
  }, [id]);

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => { setEditMode(false); setFormValues(player || {}); };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleSave = async () => {
    if (!player) return;
    try {
      await playerApi.update(player.id, formValues);
      setEditMode(false);
      setPlayer({ ...player, ...formValues } as Player);
    } catch (e) {
      alert('Failed to update player');
    }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!player) return <Box sx={{ p: 3, color: 'error.main' }}>Player not found</Box>;

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="outlined" component={Link} to="/players">Back to Players</Button>
        <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>{player.name}</Typography>
      </Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar src={player.image || undefined} sx={{ width: 80, height: 80, fontSize: 36 }}>
              {player.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">{player.name}</Typography>
              <Typography variant="body2">Role: {player.role || 'N/A'}</Typography>
              <Typography variant="body2">Batting: {player.batting || 'N/A'}</Typography>
              <Typography variant="body2">Bowling: {player.bowling || 'N/A'}</Typography>
              <Typography variant="body2">Country (team): {player.country || 'N/A'}</Typography>
              <Typography variant="body2">Nationality: {player.nationality_name || 'N/A'}</Typography>
              <Typography variant="body2">Gender: {player.gender || 'N/A'}</Typography>
              <Typography variant="body2">International: {player.is_international ? 'Yes' : 'No'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="contained" color="primary" onClick={handleEdit} disabled={editMode}>Edit</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Divider sx={{ mb: 3 }} />
      {editMode && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Edit Player Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="name" label="Name" value={formValues.name || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="role" label="Role" value={formValues.role || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="batting" label="Batting" value={formValues.batting || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="bowling" label="Bowling" value={formValues.bowling || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="country" label="Country" value={formValues.country || ''} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="gender" label="Gender" value={formValues.gender || ''} onChange={handleChange} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
              <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Last 10 Batting Innings</Typography>
              {loadingBat ? (
                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr>
                        <th>Match</th>
                        <th>Inning</th>
                        <th>Runs</th>
                        <th>Balls</th>
                        <th>SR</th>
                        <th>4s</th>
                        <th>6s</th>
                        <th>Out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batInnings.length === 0 ? (
                        <tr><td colSpan={8}>No data</td></tr>
                      ) : batInnings.map((inn) => (
                        <tr key={inn.id}>
                          <td>{inn.match_name}</td>
                          <td>{inn.inning}</td>
                          <td>{inn.runs}</td>
                          <td>{inn.balls}</td>
                          <td>{inn.strike_rate}</td>
                          <td>{inn.fours}</td>
                          <td>{inn.sixes}</td>
                          <td>{inn.is_out ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Last 10 Bowling Innings</Typography>
              {loadingBowl ? (
                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr>
                        <th>Match</th>
                        <th>Inning</th>
                        <th>Overs</th>
                        <th>Balls</th>
                        <th>Runs</th>
                        <th>Wickets</th>
                        <th>Eco</th>
                        <th>4s</th>
                        <th>6s</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bowlInnings.length === 0 ? (
                        <tr><td colSpan={9}>No data</td></tr>
                      ) : bowlInnings.map((inn) => (
                        <tr key={inn.id}>
                          <td>{inn.match_name}</td>
                          <td>{inn.inning}</td>
                          <td>{inn.overs}</td>
                          <td>{inn.balls}</td>
                          <td>{inn.conceded}</td>
                          <td>{inn.wickets}</td>
                          <td>{inn.economy}</td>
                          <td>{inn.fours}</td>
                          <td>{inn.sixes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlayerView; 